import { createContext, use, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AppClient, DemoData } from '@pointfive/contracts';
import { createHttpClient } from '@pointfive/client';
import { createSimClient } from '@pointfive/sim'; // eslint-disable-line no-restricted-imports -- the one place the sim link is bound

/** The one place a transport is chosen (SM-P2-07).
 *
 *  `VITE_API_BINDING=http` points the app at the real server: six families answer from MySQL through the kernel
 *  cascade, and the rest still answer from the sim *on the server side*, labelled as such. `sim` keeps the whole
 *  aggregate in-process, which is what a bare checkout and the design-fidelity suite use.
 *
 *  The swap is safe because the aggregate is identical either way — asserted directly by the dataset-parity test, and
 *  confirmed in pixels by the visual-regression suite. Nothing downstream of here knows which binding it got. */
type Binding = 'sim' | 'http';

const binding: Binding = (import.meta.env['VITE_API_BINDING'] as Binding | undefined) ?? 'sim';
const apiOrigin = (import.meta.env['VITE_API_ORIGIN'] as string | undefined) ?? 'http://localhost:3000';

function createClient(): AppClient & { demo: { dataset: () => Promise<DemoData> } } {
  if (binding === 'http') return createHttpClient({ baseUrl: apiOrigin });
  return createSimClient({ seed: import.meta.env['VITE_SIM_SEED'] as string | undefined });
}

export const client = createClient();
/** Which transport this build is talking to — read by `/dev/console` and reported in checkpoint evidence. */
export const apiBinding: Binding = binding;
/** Where the API is, for the two things that cannot go through the typed client: the OIDC redirect and sign-out. */
export { apiOrigin };

/** The authenticated dataset, fetched when a route that needs it is matched — and not before.
 *
 *  This used to be a module-level promise, so importing the app started it: the whole authenticated aggregate was
 *  requested before any route had been matched, for `/card/:token` and `/portal` as much as for the trader's own
 *  screens. A public visitor's first act was to ask for a private dataset. Now the promise is created on first use,
 *  and the only routes that use it are behind the session guard (WP-02).
 *
 *  Still lazily memoised rather than per-mount: `use()` needs a stable promise across renders, and one dataset per
 *  page load is the intent. `resetDemoData` exists so a sign-out does not leave one tenant's figures in memory for
 *  the next person to sign in on the same tab. */
let datasetPromise: Promise<DemoData> | null = null;

function dataset(): Promise<DemoData> {
  datasetPromise ??= client.demo.dataset();
  return datasetPromise;
}

export function resetDemoData(): void {
  datasetPromise = null;
}

/** Re-fetches the authenticated dataset IN PLACE (mandate §1: a successful link appears immediately). The error
 *  boundary's remount handles failures; this handles success — the provider re-renders, `use()` suspends on the
 *  fresh promise, and every consumer re-reads the server's answer. */
let bumpEpoch: (() => void) | null = null;
export function refreshDemoData(): void {
  datasetPromise = null;
  bumpEpoch?.();
}

/** Exported so the public surfaces can fill the same context from a different source. One context, because the
 *  screens are the same screens and should not have to know which loader put the data there. */
export const DataContext = createContext<DemoData | null>(null);

export function DataProvider({ children }: { children: ReactNode }) {
  const [, setEpoch] = useState(0);
  useEffect(() => {
    bumpEpoch = () => setEpoch((e) => e + 1);
    return () => {
      bumpEpoch = null;
    };
  }, []);
  const data = use(dataset());
  return <DataContext.Provider value={data}>{children}</DataContext.Provider>;
}
export function useDemoData(): DemoData {
  const d = useContext(DataContext);
  if (!d) throw new Error('useDemoData outside DataProvider');
  return d;
}
